import { LitElement, html, PropertyValues } from 'lit';
import { property, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { EnumListItem, GroupType } from '../../../ui/gzEnumList/types';
import { WEB_COMPONENT_GZ_ENUM_LIST } from '../../../webcomponents.constants';
import { GzEnumListWebComponent } from '../../../ui/gzEnumList';
import { GridSettingsEvents } from '../types';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { GzCheckboxWebComponent } from '../../../ui/gzCheckbox';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { dataGridStrings } from '../../utils/gridStrings';

export class GridSettingsWebComponent extends LitElement {
  @property({ type: Array, attribute: true, reflect: true })
  gridColumns: EnumListItem[] = [];

  @property({ type: Boolean, reflect: true })
  canSelectAll = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  compactView;

  @property({ type: String, attribute: true, reflect: true })
  groupType: GroupType = GroupType.DEFAULT;

  @query(WEB_COMPONENT_GZ_ENUM_LIST)
  private readonly enumList!: GzEnumListWebComponent;

  @query('#toggle-grid-compact')
  private compactViewToggle!: GzCheckboxWebComponent;

  @query('#list-content')
  private listContent: HTMLElement;

  @query('#actionButton')
  private actionButton: GzButtonWebComponent;

  static get styles() {
    return styles;
  }

  updated(properties: PropertyValues): void {
    if (properties.has('gridColumns')) {
      this.enumList.setAttribute('filterOptions', JSON.stringify(this.gridColumns));
    }
  }

  render() {
    return html`
      <div class="grid-settings--container">
        <div class="grid-settings--header">
          <gz-title fontSize="16" fontWeight="500" class="grid-settings--title"
            >${dataGridStrings().settingsTitle}</gz-title
          >
        </div>
        <div class="grid-settings--content">
          <div class="grid-columns--toggle-view">
            <gz-checkbox
              id="toggle-grid-compact"
              .checked="${this.compactView}"
              label="${dataGridStrings().compactViewString}"
            ></gz-checkbox>
          </div>
          <div id="list-content">
            <gz-enum-list
              .filterOptions="${this.gridColumns}"
              searchPlaceholderString="${dataGridStrings().searchColumnsPlaceholderString}"
              noSearchResultsString="${dataGridStrings().noColumnsFound}"
              .canSelectAll="${this.canSelectAll}"
              allItemsLabel="${dataGridStrings().selectAll}"
              .groupType="${this.groupType}"
              @optionChanged="${this.updateActionButtonState}"
            ></gz-enum-list>
          </div>
        </div>

        <div class="grid-settings--footer">
          <gz-button id="actionButton" ?disabled="${isEmpty(this.gridColumns)}" @click="${this.handleConfirmSelection}"
            >${dataGridStrings().actionButtonString}
          </gz-button>
        </div>
      </div>
    `;
  }

  private handleConfirmSelection() {
    /**
     * When the Apply button is clicked:
     * 1. get the selected checkboxes from the filterable list
     * 2. update the selectedOptions property on the enumList component
     * 3. emit the selection event
     */
    const checkedOptions = this.enumList.getCheckedOptions();

    this.enumList.setSelectedOptions(checkedOptions);
    this.compactView = this.compactViewToggle.checked;

    const selectedColumns = checkedOptions.map(option => option.value);

    const event = new CustomEvent(GridSettingsEvents.SIDE_PANEL_SETTINGS_UPDATED, {
      detail: {
        settings: {
          selectedColumns: selectedColumns,
          isCompactViewChecked: this.compactViewToggle.checked,
        },
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  public updateEnumListUiState() {
    if (this.enumList) {
      this.enumList.matchCheckboxesToSelectedOptions();
      this.enumList.updateSelectAllCheckboxesState();
    }
  }

  public resetSettingsToInitialState(e: Event) {
    if (this.enumList) {
      this.enumList.filterOptions = [...this.gridColumns];
      this.enumList.searchInput.clearInputValue(e);
      this.updateActionButtonState();
    }
    if (this.compactViewToggle) {
      this.compactViewToggle.checked = this.compactView;
    }
  }

  private updateActionButtonState() {
    this.actionButton.disabled = isEmpty(this.enumList?.getCheckedOptions());
  }
}
